<template>
  <v-container>
    <template v-if="filters.length">
      <component
        :is="getFilterComponent(filter)"
        v-for="filter in filters"
        :key="filter.name"
        :filter="filter"
        :values="localValues"
        class="ml-1 mt-6 mb-4 d-inline"
        @apply="apply"
      />
      <v-btn
        class="float-right"
        depressed
        small
        @click="clearAll"
      >
        <v-icon
          left
          small
        >
          mdi-close
        </v-icon>
        Clear filters
      </v-btn>
      <v-divider class="mt-2 mb-4" />
    </template>
    <v-row
      justify="end"
    >
      <v-col
        v-if="ordering.length > 0"
        cols="10"
        lg="2"
      >
        <v-autocomplete
          v-model="order.by"
          label="Order by"
          :items="ordering"
        />
      </v-col>
      <v-col
        v-if="ordering.length > 0"
        cols="2"
        lg="2"
        :offset-lg="hideSearch ? 9 : 0"
      >
        <v-btn-toggle
          v-if="order.by"
          v-model="order.desc"
          class="mt-5"
        >
          <v-btn
            small
            :value="false"
          >
            <v-icon>mdi-arrow-up</v-icon>
          </v-btn>
          <v-btn
            small
            :value="true"
          >
            <v-icon>mdi-arrow-down</v-icon>
          </v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col
        v-if="!hideSearch"
        cols="12"
        lg="3"
        class="ml-auto"
      >
        <v-text-field
          v-model="localValues.search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          float="right"
        />
      </v-col>
      <v-col
        v-if="$slots.actions"
        cols="12"
        lg="auto"
      >
        <slot name="actions" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import TextFilter from './FiltersText.vue';
import BooleanFilter from './FiltersBoolean.vue';
import DateFilter from './FiltersDate.vue';
import SelectFilter from './FiltersSelect.vue';
import DebouncedSelectFilter from './FiltersDebouncedSelect.vue';
import DatetimeRangeFilter from './FiltersDatetimeRange.vue';

export default {
  name: 'DataIteratorFilters',
  props: {
    value: {
      type: Object,
      required: true,
    },
    filters: {
      type: Array,
      default: () => [],
    },
    ordering: {
      type: Array,
      default: () => [],
    },
    hideSearch: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    localValues: {
      search: undefined,
      ordering: undefined,
    },
    displayValues: {},
    order: {
      by: '',
      desc: false,
    },
  }),
  computed: {
  },
  watch: {
    localValues: {
      handler(val) {
        this.$emit('input', val);
      },
      deep: true,
    },
    order: {
      handler(val) {
        const descChar = val.desc ? '-' : '';
        this.localValues.ordering = this.$set(
          this.localValues,
          'ordering',
          val.by ? `${descChar}${val.by}` : undefined,
        );
      },
      deep: true,
    },
  },
  created() {
    const queryValues = {};
    queryValues.search = this.$route.query.search;
    this.filters.forEach((filter) => {
      if (['boolean', 'date'].includes(filter.type)) {
        queryValues[filter.name] = this.$route.query[filter.name];
      } else if (filter.type === 'text') {
        ['', '__iexact', '__contains', '__icontains'].forEach((lookupExpr) => {
          const key = `${filter.name}${lookupExpr}`;
          queryValues[key] = this.$route.query[key];
        });
      } else if (['select', 'debounced-select'].includes(filter.type)) {
        const value = this.$route.query[filter.name];
        if (filter.multiple && value && !(value instanceof Array)) {
          queryValues[filter.name] = [value];
        } else {
          queryValues[filter.name] = value;
        }
      } else if (filter.type === 'datetime-range') {
        const afterDate = this.$route.query[`${filter.name}_after`];
        const beforeDate = this.$route.query[`${filter.name}_before`];
        if (afterDate) {
          queryValues[`${filter.name}_after`] = afterDate;
        }
        if (beforeDate) {
          queryValues[`${filter.name}_before`] = beforeDate;
        }
      }
    });
    this.localValues = { ...this.localValues, ...queryValues };
  },
  methods: {
    getFilterComponent(filter) {
      return {
        text: TextFilter,
        boolean: BooleanFilter,
        date: DateFilter,
        select: SelectFilter,
        'debounced-select': DebouncedSelectFilter,
        'datetime-range': DatetimeRangeFilter,
      }[filter.type];
    },
    apply(values) {
      this.localValues = { ...this.localValues, ...values };
    },
    clearAll() {
      this.editingFilter = null;
      this.localValues = {};
      this.order.by = '';
    },
  },
};
</script>
